import * as React from 'react';
import { FocusZone, FocusZoneDirection, Separator } from '@fluentui/react';

import { ISectionedListProps } from './SectionedList.types';
import { Section } from './Section';
import { IGroupedComboBoxGroup, isGroupCollapsed } from 'grouped-combobox-core';
import { ISection } from './Section.types';
import { useSectionKeyDownHandler } from './SectionKeyHandler';
import { getListStyle } from './SectionedList.styles';

export const SectionedList = React.forwardRef<HTMLDivElement, ISectionedListProps>(
    (props: ISectionedListProps, ref) => {
        const {
            groups,
            headerStyles,
            sectionClassNames,
            separatorStyles,
            componentRef,
            itemStyles,
            onGroupToggled,
            selectedKey,
            width,
            onDismiss,
            parentBottomPadding,
            isFiltering,
            groupStates,
            itemHeight,
        } = props;

        console.assert(
            groups.find((g) => g.items.length === 0) === undefined,
            "'groups' must not contain any empty groups."
        );

        const [, forceRefresh] = React.useReducer((x) => x + 1, 0);

        const onGroupToggledCallback = React.useCallback(
            (groupKey: string, isCollapsed: boolean) => {
                groupStates.set(groupKey, isCollapsed);
                // Because we changed groupStates, but it's not passed to the Section components as props.
                // Which means those component do not rerender. Hence we use this trick to force a rerender.
                forceRefresh();

                onGroupToggled?.(groupKey, isCollapsed);
            },
            [groupStates, onGroupToggled]
        );

        // Hold the array of refs to all the groups.
        // The `groups` passed to this component is guaranteed to not have any empty group.
        const sectionRefs = React.useMemo<React.RefObject<ISection>[]>(() => {
            const refs: React.RefObject<ISection>[] = [];
            for (let i = 0; i < groups.length; ++i) {
                refs.push(React.createRef<ISection>());
            }
            return refs;
        }, [groups.length]);

        const keyDownHandler = useSectionKeyDownHandler(sectionRefs, groups, onDismiss);

        const listStyle = React.useMemo(() => getListStyle(width), [width]);

        if (groups.length === 0) {
            return null;
        }

        return (
            <FocusZone
                style={listStyle}
                componentRef={componentRef}
                direction={FocusZoneDirection.vertical}
                allowFocusRoot={true}
                isCircularNavigation={false}
                checkForNoWrap={true}
                onKeyDown={keyDownHandler}
                elementRef={ref}
                as="div"
            >
                {groups.map((group: IGroupedComboBoxGroup, index: number) => {
                    return (
                        <React.Fragment key={group.groupKey}>
                            {!!index && <Separator styles={separatorStyles} />}
                            <Section
                                {...group}
                                componentRef={sectionRefs[index]}
                                classNames={sectionClassNames}
                                headerStyles={headerStyles}
                                itemStyles={itemStyles}
                                onGroupToggled={onGroupToggledCallback}
                                selectedKey={selectedKey}
                                parentBottomPadding={parentBottomPadding}
                                isCollapsed={isGroupCollapsed(group, groupStates, isFiltering)}
                                itemHeight={itemHeight}
                            />
                        </React.Fragment>
                    );
                })}
            </FocusZone>
        );
    }
);

SectionedList.displayName = 'SectionedList';
